"use client";

import { faCloudUpload, faUpload, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef, useCallback, useId, useRef, useState } from "react";
import { DropZone as AriaDropZone, Text as AriaText } from "react-aria-components";
import { tv } from "tailwind-variants";

import { cn } from "../../utils/cn";
import { Button } from "../button/Button";

import type { ReactNode } from "react";
import type { DropZoneProps as AriaDropZoneProps } from "react-aria-components";

// Types for file handling (compatible with antd patterns)
export interface UploadFile {
  uid: string;
  name: string;
  size?: number;
  type?: string;
  status?: "uploading" | "done" | "error" | "removed";
  percent?: number;
  originFileObj?: File;
  response?: unknown;
  error?: unknown;
}

export interface UploadRequestOption {
  file: File;
  onSuccess?: (response?: unknown) => void;
  onError?: (error: unknown) => void;
  onProgress?: (percent: number) => void;
}

export interface FileUploadProps {
  /** Accept file types (e.g., ".json", "image/*", ".pdf,.doc") */
  accept?: string;
  /** Allow multiple file selection */
  multiple?: boolean;
  /** Maximum number of files allowed */
  maxCount?: number;
  /** Controlled file list */
  fileList?: UploadFile[];
  /** Default file list for uncontrolled mode */
  defaultFileList?: UploadFile[];
  /** Whether to show file list */
  showUploadList?: boolean;
  /** Disable upload */
  disabled?: boolean;
  /** Custom request handler (for manual upload) */
  customRequest?: (options: UploadRequestOption) => void;
  /** Called before upload - return false to prevent upload, true to proceed, or Promise */
  beforeUpload?: (file: File, fileList: File[]) => boolean | Promise<void | boolean>;
  /** Called when files change */
  onChange?: (info: { file: UploadFile; fileList: UploadFile[] }) => void;
  /** Called when a file is removed */
  onRemove?: (file: UploadFile) => boolean | void | Promise<boolean | void>;
  /** Called when files are dropped */
  onDrop?: (files: File[]) => void;
  /** Children to render inside the upload area */
  children?: ReactNode;
  /** Additional class name */
  className?: string;
  /** Custom class names for slots */
  classNames?: {
    base?: string;
    dropZone?: string;
    fileList?: string;
    fileItem?: string;
  };
  /** Whether the upload appears as a dragger (drop zone) */
  dragger?: boolean;
  /** Name attribute for the input */
  name?: string;
  /** Directory upload support */
  directory?: boolean;
}

export interface FileUploadDraggerProps extends Omit<FileUploadProps, "dragger"> {
  /** Description text shown in dragger */
  description?: ReactNode;
  /** Icon to show in dragger */
  icon?: ReactNode;
}

const fileUploadVariants = tv({
  slots: {
    base: "file-upload",
    trigger:
      "inline-flex cursor-pointer items-center justify-center rounded border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700",
    dropZone:
      "flex min-h-[150px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-center transition-colors hover:border-blue-400 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:hover:border-blue-500 dark:hover:bg-gray-700",
    dropZoneActive: "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20",
    dropZoneDisabled:
      "cursor-not-allowed opacity-50 hover:border-gray-300 hover:bg-gray-50 dark:hover:border-gray-600 dark:hover:bg-gray-800",
    fileList: "mt-2 space-y-2",
    fileItem:
      "flex items-center justify-between rounded border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800",
    fileItemName: "truncate text-gray-700 dark:text-gray-200",
    fileItemActions: "ml-2 flex items-center gap-2",
    fileItemRemove:
      "cursor-pointer text-gray-400 transition-colors hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400",
    icon: "mb-2 text-3xl text-gray-400 dark:text-gray-500",
    text: "text-sm text-gray-500 dark:text-gray-400",
    hint: "mt-1 text-xs text-gray-400 dark:text-gray-500",
  },
});

// Helper to convert File to UploadFile
const fileToUploadFile = (file: File): UploadFile => ({
  uid: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  name: file.name,
  size: file.size,
  type: file.type,
  status: "done",
  originFileObj: file,
});

// Internal DraggerDropZone component that handles click-to-upload with native input
interface DraggerDropZoneProps {
  className?: string;
  classNames?: FileUploadProps["classNames"];
  disabled?: boolean;
  accept?: string;
  multiple?: boolean;
  onSelect: (files: FileList | null) => void;
  onDrop: (e: Parameters<NonNullable<AriaDropZoneProps["onDrop"]>>[0]) => void;
  children?: ReactNode;
  fileList: UploadFile[];
  showUploadList?: boolean;
  onRemove: (file: UploadFile) => void;
}

const DraggerDropZone = forwardRef<HTMLDivElement, DraggerDropZoneProps>(
  (
    {
      className,
      classNames,
      disabled = false,
      accept,
      multiple = false,
      onSelect,
      onDrop,
      children,
      fileList,
      showUploadList,
      onRemove,
    },
    ref
  ) => {
    const styles = fileUploadVariants();
    const inputRef = useRef<HTMLInputElement>(null);
    const inputId = useId();

    const handleClick = useCallback(() => {
      if (!disabled && inputRef.current) {
        inputRef.current.click();
      }
    }, [disabled]);

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onSelect(e.target.files);
        // Reset the input so the same file can be selected again
        e.target.value = "";
      },
      [onSelect]
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if ((e.key === "Enter" || e.key === " ") && !disabled) {
          e.preventDefault();
          inputRef.current?.click();
        }
      },
      [disabled]
    );

    return (
      <div ref={ref} className={cn(styles.base(), className, classNames?.base)}>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={handleInputChange}
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
        />
        <AriaDropZone
          onDrop={onDrop}
          isDisabled={disabled}
          className={({ isDropTarget }) =>
            cn(
              styles.dropZone(),
              isDropTarget && styles.dropZoneActive(),
              disabled && styles.dropZoneDisabled(),
              classNames?.dropZone
            )
          }
        >
          <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className="flex w-full cursor-pointer flex-col items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed"
            aria-disabled={disabled}
          >
            {children ?? (
              <>
                <FontAwesomeIcon icon={faCloudUpload} className={styles.icon()} />
                <AriaText slot="label" className={styles.text()}>
                  Click or drag file to this area to upload
                </AriaText>
                {accept && <span className={styles.hint()}>Supported formats: {accept}</span>}
              </>
            )}
          </div>
        </AriaDropZone>
        {showUploadList && fileList.length > 0 && (
          <div className={cn(styles.fileList(), classNames?.fileList)}>
            {fileList.map(file => (
              <div key={file.uid} className={cn(styles.fileItem(), classNames?.fileItem)}>
                <span className={styles.fileItemName()}>{file.name}</span>
                <div className={styles.fileItemActions()}>
                  {file.status === "uploading" && (
                    <span className="text-xs text-blue-500">{file.percent ?? 0}%</span>
                  )}
                  {file.status === "error" && <span className="text-xs text-red-500">Error</span>}
                  <Button
                    variant="light"
                    isIconOnly
                    size="xs"
                    className={styles.fileItemRemove()}
                    onPress={() => onRemove(file)}
                    aria-label={`Remove ${file.name}`}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
);

DraggerDropZone.displayName = "DraggerDropZone";

// Internal ClickUploadTrigger component that handles click-to-upload with native input
interface ClickUploadTriggerProps {
  className?: string;
  classNames?: FileUploadProps["classNames"];
  disabled?: boolean;
  accept?: string;
  multiple?: boolean;
  onSelect: (files: FileList | null) => void;
  children?: ReactNode;
  fileList: UploadFile[];
  showUploadList?: boolean;
  onRemove: (file: UploadFile) => void;
}

const ClickUploadTrigger = forwardRef<HTMLDivElement, ClickUploadTriggerProps>(
  (
    {
      className,
      classNames,
      disabled = false,
      accept,
      multiple = false,
      onSelect,
      children,
      fileList,
      showUploadList,
      onRemove,
    },
    ref
  ) => {
    const styles = fileUploadVariants();
    const inputRef = useRef<HTMLInputElement>(null);
    const inputId = useId();

    const handleClick = useCallback(() => {
      if (!disabled && inputRef.current) {
        inputRef.current.click();
      }
    }, [disabled]);

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onSelect(e.target.files);
        // Reset the input so the same file can be selected again
        e.target.value = "";
      },
      [onSelect]
    );

    return (
      <div ref={ref} className={cn(styles.base(), className, classNames?.base)}>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={handleInputChange}
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
        />
        {children ? (
          // Use onClickCapture so the handler fires during the capture phase, before
          // react-aria's usePress can call stopPropagation() on the native click event.
          <div onClickCapture={handleClick} className="inline-block cursor-pointer">
            {children}
          </div>
        ) : (
          <Button variant="default" isDisabled={disabled} onPress={handleClick}>
            <FontAwesomeIcon icon={faUpload} />
            Upload
          </Button>
        )}
        {showUploadList && fileList.length > 0 && (
          <div className={cn(styles.fileList(), classNames?.fileList)}>
            {fileList.map(file => (
              <div key={file.uid} className={cn(styles.fileItem(), classNames?.fileItem)}>
                <span className={styles.fileItemName()}>{file.name}</span>
                <div className={styles.fileItemActions()}>
                  {file.status === "uploading" && (
                    <span className="text-xs text-blue-500">{file.percent ?? 0}%</span>
                  )}
                  {file.status === "error" && <span className="text-xs text-red-500">Error</span>}
                  <Button
                    variant="light"
                    isIconOnly
                    size="xs"
                    className={styles.fileItemRemove()}
                    onPress={() => onRemove(file)}
                    aria-label={`Remove ${file.name}`}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
);

ClickUploadTrigger.displayName = "ClickUploadTrigger";

/**
 * FileUpload component for uploading files.
 * Supports click-to-upload and drag-and-drop modes.
 */
const FileUploadBase = forwardRef<HTMLDivElement, FileUploadProps>(
  (
    {
      accept,
      multiple = false,
      maxCount,
      fileList: controlledFileList,
      defaultFileList = [],
      showUploadList = true,
      disabled = false,
      customRequest,
      beforeUpload,
      onChange,
      onRemove,
      onDrop,
      children,
      className,
      classNames,
      dragger = false,
    },
    ref
  ) => {
    // Internal file list state for uncontrolled mode
    const [internalFileList, setInternalFileList] = useState<UploadFile[]>(defaultFileList);
    const fileList = controlledFileList ?? internalFileList;

    // Update file list (handles both controlled and uncontrolled)
    const updateFileList = useCallback(
      (newFiles: UploadFile[], changedFile: UploadFile) => {
        if (controlledFileList === undefined) {
          setInternalFileList(newFiles);
        }
        onChange?.({ file: changedFile, fileList: newFiles });
      },
      [controlledFileList, onChange]
    );

    // Process files after selection
    const processFiles = useCallback(
      async (files: File[]) => {
        // Filter by maxCount if specified
        let filesToProcess = files;
        if (maxCount !== undefined) {
          const remainingSlots = maxCount - fileList.length;
          if (remainingSlots <= 0) return;
          filesToProcess = files.slice(0, remainingSlots);
        }

        // Accumulate files to avoid closure issues when processing multiple files
        let currentList = [...fileList];

        for (const file of filesToProcess) {
          // Call beforeUpload if provided
          if (beforeUpload) {
            try {
              const result = await beforeUpload(file, filesToProcess);
              if (result === false) {
                continue; // Skip this file
              }
            } catch {
              continue; // Skip on error
            }
          }

          const uploadFile = fileToUploadFile(file);

          // If customRequest is provided, use it for upload
          if (customRequest) {
            uploadFile.status = "uploading";
            currentList = [...currentList, uploadFile];
            updateFileList(currentList, uploadFile);

            customRequest({
              file,
              onSuccess: response => {
                uploadFile.status = "done";
                uploadFile.response = response;
                // Note: We update with a new array to trigger re-render
                const updatedList = currentList.map(f =>
                  f.uid === uploadFile.uid ? uploadFile : f
                );
                updateFileList(updatedList, uploadFile);
              },
              onError: error => {
                uploadFile.status = "error";
                uploadFile.error = error;
                const updatedList = currentList.map(f =>
                  f.uid === uploadFile.uid ? uploadFile : f
                );
                updateFileList(updatedList, uploadFile);
              },
              onProgress: percent => {
                uploadFile.percent = percent;
                const updatedList = currentList.map(f =>
                  f.uid === uploadFile.uid ? uploadFile : f
                );
                updateFileList(updatedList, uploadFile);
              },
            });
          } else {
            // Just add to file list without upload
            currentList = [...currentList, uploadFile];
            updateFileList(currentList, uploadFile);
          }
        }
      },
      [fileList, maxCount, beforeUpload, customRequest, updateFileList]
    );

    // Handle file selection from FileTrigger
    const handleSelect = useCallback(
      (files: FileList | null) => {
        if (!files || disabled) return;
        const fileArray = Array.from(files);
        processFiles(fileArray);
      },
      [disabled, processFiles]
    );

    // Handle drop
    const handleDrop = useCallback(
      (e: Parameters<NonNullable<AriaDropZoneProps["onDrop"]>>[0]) => {
        if (disabled) return;
        const filePromises: Promise<File>[] = [];
        for (const item of e.items) {
          if (item.kind === "file") {
            filePromises.push(item.getFile());
          }
        }

        Promise.all(filePromises).then(resolvedFiles => {
          onDrop?.(resolvedFiles);
          processFiles(resolvedFiles);
        });
      },
      [disabled, onDrop, processFiles]
    );

    // Handle file removal
    const handleRemove = useCallback(
      async (file: UploadFile) => {
        if (onRemove) {
          const result = await onRemove(file);
          if (result === false) return;
        }

        const newList = fileList.filter(f => f.uid !== file.uid);
        const removedFile = { ...file, status: "removed" as const };
        updateFileList(newList, removedFile);
      },
      [fileList, onRemove, updateFileList]
    );

    // Dragger mode - full drop zone
    if (dragger) {
      return (
        <DraggerDropZone
          ref={ref}
          className={className}
          classNames={classNames}
          disabled={disabled}
          accept={accept}
          multiple={multiple}
          onSelect={handleSelect}
          onDrop={handleDrop}
          fileList={fileList}
          showUploadList={showUploadList}
          onRemove={handleRemove}
        >
          {children}
        </DraggerDropZone>
      );
    }

    // Regular click-to-upload mode
    return (
      <ClickUploadTrigger
        ref={ref}
        className={className}
        classNames={classNames}
        disabled={disabled}
        accept={accept}
        multiple={multiple}
        onSelect={handleSelect}
        fileList={fileList}
        showUploadList={showUploadList}
        onRemove={handleRemove}
      >
        {children}
      </ClickUploadTrigger>
    );
  }
);

FileUploadBase.displayName = "FileUpload";

/**
 * FileUpload.Dragger - Drag-and-drop file upload zone
 */
const FileUploadDraggerBase = forwardRef<HTMLDivElement, FileUploadDraggerProps>(
  ({ description, icon, children, ...props }, ref) => {
    const styles = fileUploadVariants();

    // Note: The children here will be wrapped in a button by the parent FileUploadBase
    // when dragger mode is enabled, so we just need to provide the content
    return (
      <FileUploadBase ref={ref} {...props} dragger>
        {children ?? (
          <>
            {icon ?? <FontAwesomeIcon icon={faCloudUpload} className={styles.icon()} />}
            <AriaText slot="label" className={styles.text()}>
              {description ?? "Click or drag file to this area to upload"}
            </AriaText>
            {props.accept && (
              <span className={styles.hint()}>Supported formats: {props.accept}</span>
            )}
          </>
        )}
      </FileUploadBase>
    );
  }
);

FileUploadDraggerBase.displayName = "FileUpload.Dragger";

// Compound component exports
export const FileUpload = Object.assign(FileUploadBase, {
  Dragger: FileUploadDraggerBase,
});

export const FileUploadDragger = FileUploadDraggerBase;

export default FileUpload;
